import React from 'react';
import {
  Link as FluentLink,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { Person24Regular, Open16Regular } from '@fluentui/react-icons';
import { CustomerSearchInput } from '../CustomerSearchInput';
import { SectionShell } from './SectionShell';

interface Customer {
  id: string;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  municipality?: string;
  zipCode?: string;
  country?: string;
}

interface CustomerSectionProps {
  selectedCustomer: Customer | null;
  onCustomerSelect: (customer: Customer) => void;
  onCustomerClear: () => void;
  /** When provided, renders an Add-customer icon button to the right of the
   *  search field. Click opens the caller's create-customer drawer. */
  onAddCustomer?: () => void;
  showIcon?: boolean;
  /** Read-only mode for project child orders: the customer is owned by the
   *  parent project, so render a static card + manage link instead of search. */
  readOnly?: boolean;
  manageLabel?: string;
  onManageClick?: () => void;
}

const useStyles = makeStyles({
  card: {
    display: 'flex',
    alignItems: 'center',
    // Shared min-height so the Customer card lines up exactly with the sibling
    // Billing address card (which can be a single-line empty state). Keep this
    // value in sync with BillingAddressCard.
    minHeight: '68px',
    boxSizing: 'border-box',
    ...shorthands.gap('12px'),
    ...shorthands.padding('12px', '16px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  textCol: { minWidth: 0, flex: 1 },
  name: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  email: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  manageRow: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('6px'),
    marginTop: '8px',
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
  },
});

const CustomerSectionBase: React.FC<CustomerSectionProps> = ({
  selectedCustomer,
  onCustomerSelect,
  onCustomerClear,
  onAddCustomer,
  showIcon = true,
  readOnly = false,
  manageLabel,
  onManageClick,
}) => {
  const styles = useStyles();
  if (readOnly) {
    return (
      <SectionShell title="Customer" icon={<Person24Regular />} showIcon={showIcon}>
        <div className={styles.card}>
          <Person24Regular style={{ color: tokens.colorNeutralForeground3, flexShrink: 0 }} />
          <div className={styles.textCol}>
            <div className={styles.name}>{selectedCustomer?.name || 'No customer'}</div>
            {selectedCustomer?.email && <div className={styles.email}>{selectedCustomer.email}</div>}
          </div>
        </div>
        {onManageClick && (
          <FluentLink as="button" className={styles.manageRow} onClick={onManageClick}>
            {manageLabel || 'Manage on project'}
            <Open16Regular />
          </FluentLink>
        )}
      </SectionShell>
    );
  }
  return (
    <SectionShell title="Customer" icon={<Person24Regular />} showIcon={showIcon}>
      <CustomerSearchInput
        selectedCustomer={selectedCustomer}
        onCustomerSelect={onCustomerSelect}
        onCustomerClear={onCustomerClear}
        onAddCustomer={onAddCustomer}
        size="large"
      />
    </SectionShell>
  );
};

export const CustomerSection = React.memo(CustomerSectionBase);
export default CustomerSection;
